import { currentTheme } from "../lib/theme";

const SHARED_THEME = {
  accentColor: "lime",
  grayColor: "gray",
  dangerColor: "red",
  warningColor: "amber",
  successColor: "green",
  infoColor: "sky",
};

const SHARED_VARIABLES = {
  "--radius": "10px",
  "--default-font-family": '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  "--heading-font-family": '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  "--strong-font-family": '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  "--default-letter-spacing": "-0.01em",
  "--heading-letter-spacing": "-0.035em",
  "--table-row-background-color": "transparent",
};

const SHARED_CLASSES = {
  "whop-FieldLabel": { fontSize: "15px", fontWeight: "650" },
  "whop-FieldDescription": { fontSize: "13px", lineHeight: "1.5" },
  "whop-InputRoot": { minHeight: "48px", borderRadius: "10px" },
  "whop-Input": { padding: "12px 14px", fontSize: "15px" },
  "whop-SelectTrigger": { minHeight: "48px", padding: "10px 14px", borderRadius: "10px", fontSize: "15px" },
  "whop-SelectPopup": { borderRadius: "10px" },
  "whop-DialogContent": { borderRadius: "18px" },
  "whop-DialogHeader": { padding: "20px 22px" },
  "whop-DialogBody": { padding: "20px 22px" },
  "whop-DialogFooter": { padding: "16px 22px 20px" },
};

export const PORTAL_ELEMENTS_APPEARANCE_DARK = {
  theme: { appearance: "dark", ...SHARED_THEME },
  variables: {
    ...SHARED_VARIABLES,
    "--color-background": "#0A0A0A",
    "--color-panel-solid": "#141414",
    "--color-panel-translucent": "#141414",
    "--color-stroke": "rgba(255, 255, 255, 0.10)",
    "--accent-1": "#121502",
    "--accent-2": "#161A03",
    "--accent-3": "#1C2205",
    "--accent-4": "#232B06",
    "--accent-5": "#2C3607",
    "--accent-6": "#37430A",
    "--accent-7": "#47570C",
    "--accent-8": "#5F750F",
    "--accent-9": "#DFFF29",
    "--accent-10": "#C6E51A",
    "--accent-11": "#E6FF52",
    "--accent-12": "#F3FFB0",
    "--accent-9-contrast": "#0C0F00",
    "--gray-1": "#141414",
    "--gray-2": "#171717",
    "--gray-3": "#1B1B1B",
    "--gray-4": "#202020",
    "--gray-5": "#262626",
    "--gray-6": "#2D2D2D",
    "--gray-7": "#363636",
    "--gray-8": "#474747",
    "--gray-9": "#6C6C66",
    "--gray-10": "#7F7F79",
    "--gray-11": "#9A9A94",
    "--gray-12": "#F3F3F1",
    "--orange-9": "#D3F02F",
    "--orange-10": "#B9D90E",
    "--orange-11": "#E6FF52",
    "--orange-12": "#F3FFB0",
    "--green-9": "#DFFF29",
    "--green-10": "#C9E914",
    "--green-11": "#E6FF52",
    "--green-12": "#F3FFB0",
    "--green-a2": "rgba(255, 255, 255, 0.03)",
    "--green-surface": "rgba(223, 255, 41, 0.08)",
    "--green-9-contrast": "#0C0F00",
    "--amber-1": "#141414",
    "--amber-2": "#171717",
    "--amber-3": "#1B1B1B",
    "--amber-4": "#202020",
    "--amber-5": "#262626",
    "--amber-6": "#2D2D2D",
    "--amber-7": "#363636",
    "--amber-8": "#474747",
    "--amber-9": "#ffc53d",
    "--amber-10": "#ffba18",
    "--amber-11": "#E8B54A",
    "--amber-12": "#F5DFAE",
    "--amber-a2": "rgba(255, 255, 255, 0.03)",
    "--amber-surface": "rgba(255, 197, 61, 0.08)",
    "--amber-9-contrast": "#0C0F00",
    "--yellow-1": "#141414",
    "--yellow-2": "#171717",
    "--yellow-3": "#1B1B1B",
    "--yellow-4": "#202020",
    "--yellow-5": "#262626",
    "--yellow-6": "#2D2D2D",
    "--yellow-7": "#363636",
    "--yellow-8": "#474747",
    "--yellow-9": "#ffc53d",
    "--yellow-10": "#ffba18",
    "--yellow-11": "#E8B54A",
    "--yellow-12": "#F5DFAE",
    "--yellow-a2": "rgba(255, 255, 255, 0.03)",
    "--yellow-surface": "rgba(255, 197, 61, 0.08)",
    "--yellow-9-contrast": "#0C0F00",
  },
  classes: SHARED_CLASSES,
};

export const PORTAL_ELEMENTS_APPEARANCE_LIGHT = {
  theme: { appearance: "light", ...SHARED_THEME },
  variables: {
    ...SHARED_VARIABLES,
    "--color-background": "#F5F6F1",
    "--color-panel-solid": "#ffffff",
    "--color-panel-translucent": "#ffffff",
    "--color-stroke": "rgba(24, 24, 24, 0.10)",
    "--accent-1": "#FDFEF9",
    "--accent-2": "#FAFFE8",
    "--accent-3": "#F4FFC8",
    "--accent-4": "#EDFAAE",
    "--accent-5": "#E4F492",
    "--accent-6": "#D8EC78",
    "--accent-7": "#C8E056",
    "--accent-8": "#B3CE33",
    "--accent-9": "#DFFF29",
    "--accent-10": "#C6E51A",
    "--accent-11": "#5B7000",
    "--accent-12": "#2E3806",
    "--accent-9-contrast": "#0C0F00",
    "--gray-1": "#ffffff",
    "--gray-2": "#fafbfc",
    "--gray-3": "#f2f3f6",
    "--gray-4": "#e9ebee",
    "--gray-5": "#e1e3e8",
    "--gray-6": "#d7dade",
    "--gray-7": "#c8ccd2",
    "--gray-8": "#a8adb8",
    "--gray-9": "#7b8090",
    "--gray-10": "#6d7280",
    "--gray-11": "#565b66",
    "--gray-12": "#181818",
    "--orange-9": "#D3F02F",
    "--orange-10": "#B9D90E",
    "--orange-11": "#5B7000",
    "--orange-12": "#2E3806",
    "--green-9": "#DFFF29",
    "--green-10": "#C9E914",
    "--green-11": "#5B7000",
    "--green-12": "#2E3806",
    "--green-a2": "rgba(24, 24, 24, 0.025)",
    "--green-surface": "rgba(223, 255, 41, 0.12)",
    "--green-9-contrast": "#0C0F00",
    "--amber-1": "#FDFEF9",
    "--amber-2": "#fafbfc",
    "--amber-3": "#f2f3f6",
    "--amber-4": "#e9ebee",
    "--amber-5": "#e1e3e8",
    "--amber-6": "#d7dade",
    "--amber-7": "#c8ccd2",
    "--amber-8": "#a8adb8",
    "--amber-9": "#ffc53d",
    "--amber-10": "#ffba18",
    "--amber-11": "#ab6400",
    "--amber-12": "#4f3422",
    "--amber-a2": "rgba(24, 24, 24, 0.025)",
    "--amber-surface": "rgba(255, 197, 61, 0.08)",
    "--amber-9-contrast": "#181818",
    "--yellow-1": "#FDFEF9",
    "--yellow-2": "#fafbfc",
    "--yellow-3": "#f2f3f6",
    "--yellow-4": "#e9ebee",
    "--yellow-5": "#e1e3e8",
    "--yellow-6": "#d7dade",
    "--yellow-7": "#c8ccd2",
    "--yellow-8": "#a8adb8",
    "--yellow-9": "#ffc53d",
    "--yellow-10": "#ffba18",
    "--yellow-11": "#ab6400",
    "--yellow-12": "#4f3422",
    "--yellow-a2": "rgba(24, 24, 24, 0.025)",
    "--yellow-surface": "rgba(255, 197, 61, 0.08)",
    "--yellow-9-contrast": "#181818",
  },
  classes: SHARED_CLASSES,
};

export function portalElementsAppearance() {
  return currentTheme() === "light" ? PORTAL_ELEMENTS_APPEARANCE_LIGHT : PORTAL_ELEMENTS_APPEARANCE_DARK;
}
